import { FormControlLabel, MenuItem, Switch, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { createCategory, updateCategory } from '../../api/business/endpoints';
import type { Category, CategoryInput, CategoryTexts } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { MAX_CATEGORY_DEPTH, moveTargets } from '../../components/categoryTree';
import { FormDialog } from '../../components/FormDialog';
import { HtmlTextField } from '../../components/HtmlTextField';
import { LanguageTabs } from '../../components/LanguageTabs';
import { languageName, textChanges, textsOf } from '../../components/localisedText';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

type CategoryDialogProps = {
  companyId: string;
  tree: Array<Category>;
  /** null creates a category, under `parentId`. */
  category: Category | null;
  parentId: string | null;
  onClose: () => void;
};

const FIELDS: Array<keyof CategoryTexts> = ['name', 'description', 'slug'];

/**
 * A category: its parent — anywhere but under itself, within the depth the tree allows — and per content language
 * its name, description and address; whether the shops show it. The API checks names among siblings and slugs.
 */
export const CategoryDialog = ({ companyId, tree, category, parentId, onClose }: CategoryDialogProps) => {
  const { language, t } = useLanguage();
  const { languages, available } = useContentLanguages();
  const original: Record<keyof CategoryTexts, Record<string, string>> | null = category
    ? {
        name: textsOf(category.translations, 'name', languages),
        description: textsOf(category.translations, 'description', languages),
        slug: textsOf(category.translations, 'slug', languages),
      }
    : null;
  const [texts, setTexts] = useState<Record<keyof CategoryTexts, Record<string, string>>>(
    original ?? { name: {}, description: {}, slug: {} },
  );
  const [parent, setParent] = useState<string>(category?.parentId ?? parentId ?? '');
  const [active, setActive] = useState(category?.active ?? true);
  const [tab, setTab] = useState(languages[0] ?? 'sk');
  const code = languages.includes(tab) ? tab : languages[0];
  const isDefault = code === languages[0];
  const targets = moveTargets(tree, category);

  const mutation = useAppMutation(
    (input: CategoryInput) =>
      category ? updateCategory(companyId, category.id, input) : createCategory(companyId, input),
    ['categories', 'product', 'products'],
    onClose,
  );

  const submit = (): void => {
    const translations: Record<string, Partial<CategoryTexts>> = {};

    for (const field of FIELDS) {
      for (const [locale, text] of Object.entries(
        textChanges(texts[field], original?.[field] ?? null, languages),
      )) {
        translations[locale] = { ...(translations[locale] ?? {}), [field]: text };
      }
    }

    const parentId: string | null = parent === '' ? null : parent;

    mutation.mutate({
      translations,
      active,
      ...(category === null || parentId !== category.parentId ? { parentId } : {}),
    });
  };

  const set = (field: keyof CategoryTexts, value: string): void =>
    setTexts((previous) => ({ ...previous, [field]: { ...previous[field], [code]: value } }));

  return (
    <FormDialog
      title={category ? t('categories.edit') : t('categories.create')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={category ? t('common.save') : t('common.create')}
      isBusy={mutation.isPending || (texts.name[languages[0]] ?? '').trim() === ''}
      error={mutation.error}
    >
      <TextField
        select
        label={t('categories.parent')}
        helperText={t('categories.depthHint', { max: MAX_CATEGORY_DEPTH })}
        value={parent}
        onChange={(event) => setParent(event.target.value)}
      >
        <MenuItem value="">{t('categories.top')}</MenuItem>
        {targets.map((one) => (
          <MenuItem
            key={one.category.id}
            value={one.category.id}
            sx={{ pl: 2 + (one.category.depth - 1) * 2 }}
          >
            {one.category.name}
          </MenuItem>
        ))}
      </TextField>
      {languages.length > 1 ? (
        <LanguageTabs
          languages={languages}
          value={code}
          onChange={setTab}
          missing={(one) => (texts.name[one] ?? '').trim() === ''}
        />
      ) : null}
      <TextField
        autoFocus
        required={isDefault}
        label={
          languages.length > 1
            ? `${t('categories.name')} (${languageName(code, available, language)})`
            : t('categories.name')
        }
        helperText={languages.length > 1 && !isDefault ? t('languages.translationHint') : undefined}
        value={texts.name[code] ?? ''}
        inputProps={{ maxLength: 120 }}
        onChange={(event) => set('name', event.target.value)}
      />
      <HtmlTextField
        label={t('categories.description')}
        value={texts.description[code] ?? ''}
        onChange={(value) => set('description', value)}
        maxLength={20000}
        rows={3}
      />
      <TextField
        label={t('categories.slug')}
        helperText={t('categories.slugHint')}
        value={texts.slug[code] ?? ''}
        inputProps={{ maxLength: 128, style: { fontFamily: 'ui-monospace, monospace' } }}
        onChange={(event) => set('slug', event.target.value)}
      />
      <FormControlLabel
        control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />}
        label={t('categories.active')}
      />
      {category && category.children.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t('categories.productsHint', { direct: category.productCount, total: category.totalProductCount })}
        </Typography>
      ) : null}
    </FormDialog>
  );
};
